import { useCallback } from 'react';
import { supabase } from '@/lib/supabase/client';
import { UUID } from '@/types/common';
import {
  CreateWorkOrderRequest,
  UpdateWorkOrderRequest,
  WorkOrderStatus
} from '@/types/workOrders';
import { WorkOrderBaseState } from './workOrderBase';
import { useCreateWorkOrderOperations } from '@/hooks/useCreateWorkOrderOperations';
import { useCreateWorkOrderMaterials } from '@/hooks/useCreateWorkOrderMaterials';
import { useCreateWorkOrderQC } from '@/hooks/useCreateWorkOrderQC';

export const useWorkOrderCRUD = (baseState: WorkOrderBaseState) => {
  const { createWorkOrderOperations } = useCreateWorkOrderOperations();
  const { createWorkOrderMaterials } = useCreateWorkOrderMaterials();
  const { createWorkOrderQC } = useCreateWorkOrderQC();
  const {
    loading,
    setLoading,
    workOrders,
    setWorkOrders,
    selectedWorkOrder,
    setSelectedWorkOrder,
    toast
  } = baseState;

  /**
   * Fetch all Work Orders
   */
  const fetchWorkOrders = useCallback(async (filters?: { project_id?: UUID; status?: WorkOrderStatus }) => {
    try {
      setLoading(true);
      
      let query = supabase
        .from('work_orders_with_details')
        .select('*');
      
      if (filters?.project_id) {
        query = query.eq('project_id', filters.project_id);
      }
      
      if (filters?.status) {
        query = query.eq('status', filters.status);
      }
      
      const { data, error } = await query;
      
      if (error) throw error;
      
      setWorkOrders(data || []);
      return data;
    } catch (error: any) {
      toast({
        title: 'Error fetching work orders',
        description: error.message,
        variant: 'destructive',
      });
      return [];
    } finally {
      setLoading(false);
    }
  }, [setLoading, setWorkOrders, toast]);

  /**
   * Fetch a Work Order by ID
   */
  const fetchWorkOrderById = useCallback(async (id: UUID) => {
    try {
      setLoading(true);
      
      const { data, error } = await supabase
        .from('work_orders_with_details')
        .select('*')
        .eq('id', id)
        .single();
      
      if (error) throw error;
      
      setSelectedWorkOrder(data);
      return data;
    } catch (error: any) {
      toast({
        title: 'Error fetching work order',
        description: error.message,
        variant: 'destructive',
      });
      return null;
    } finally {
      setLoading(false);
    }
  }, [setLoading, setSelectedWorkOrder, toast]);

  /**
   * Create a new Work Order
   */
  const createWorkOrder = useCallback(async (workOrder: CreateWorkOrderRequest) => {
    try {
      setLoading(true);
      
      // Generate a work order number (if not provided)
      const workOrderWithNumber = {
        ...workOrder,
        // Add any default fields that might be required
        work_order_number: workOrder.work_order_number || `WO-${Math.floor(Math.random() * 10000).toString().padStart(4, '0')}`,
        actual_start_date: null,
        actual_end_date: null,
        created_by: (await supabase.auth.getUser()).data.user?.id,
        // Ensure dates are properly formatted as ISO strings if they aren't already
        planned_start_date: typeof workOrder.planned_start_date === 'string' 
          ? workOrder.planned_start_date
          : new Date(workOrder.planned_start_date).toISOString(),
        planned_end_date: typeof workOrder.planned_end_date === 'string'
          ? workOrder.planned_end_date
          : new Date(workOrder.planned_end_date).toISOString(),
      };

      console.log('Creating work order with data:', workOrderWithNumber);
      
      const { data, error } = await supabase
        .from('work_orders')
        .insert([workOrderWithNumber])
        .select()
        .single();
      
      if (error) {
        console.error('Error details:', error);
        throw error;
      }
      
      // After work order is created, create associated operations, materials, and QC checks if a routing and BOM are provided
      if (data) {
        // Extract selected component IDs from the request
        const selectedComponentIds = workOrder.selected_component_ids;

        // Create operations if a routing is provided
        if (workOrderWithNumber.routing_id) {
          console.log('Creating operations for new work order. Work Order ID:', data.id, 'Routing ID:', workOrderWithNumber.routing_id);
          console.log('Selected component IDs:', selectedComponentIds);
          try {
            const result = await createWorkOrderOperations(
              data.id,
              workOrderWithNumber.routing_id,
              selectedComponentIds
            );
            console.log('Create operations result:', result);
          } catch (error) {
            console.error('Error creating work order operations:', error);
            // Don't fail the whole operation if operations creation fails
          }
        }

        // Create materials if BOM and assembly are provided
        if (workOrder.bom_id && workOrder.assembly_id) {
          console.log('Creating materials for new work order. Work Order ID:', data.id, 'BOM ID:', workOrder.bom_id, 'Assembly ID:', workOrder.assembly_id);
          console.log('Selected component IDs:', selectedComponentIds);
          try {
            const result = await createWorkOrderMaterials(
              data.id,
              workOrder.bom_id,
              workOrder.assembly_id,
              workOrder.quantity,
              selectedComponentIds
            );
            console.log('Create materials result:', result);
          } catch (error) {
            console.error('Error creating work order materials:', error);
            // Don't fail the whole operation if materials creation fails
          }
        }

        // Create QC checks if a routing is provided
        if (workOrderWithNumber.routing_id) {
          console.log('Creating QC checks for new work order. Work Order ID:', data.id, 'Routing ID:', workOrderWithNumber.routing_id);
          try {
            const result = await createWorkOrderQC(data.id, workOrderWithNumber.routing_id);
            console.log('Create QC checks result:', result);
          } catch (error) {
            console.error('Error creating work order QC checks:', error);
            // Don't fail the whole operation if QC checks creation fails
          }
        }
      }
      
      toast({
        title: 'Work Order created',
        description: `Work Order ${data.work_order_number} has been created successfully.`,
        variant: 'success',
      });
      
      await fetchWorkOrders();
      return data;
    } catch (error: any) {
      console.error('Full error creating work order:', error);
      toast({
        title: 'Error creating work order',
        description: error.message,
        variant: 'destructive',
      });
      return null;
    } finally {
      setLoading(false);
    }
  }, [fetchWorkOrders, createWorkOrderOperations, setLoading, toast]);

  /**
   * Update a Work Order
   */
  const updateWorkOrder = useCallback(async (workOrder: UpdateWorkOrderRequest) => {
    try {
      setLoading(true);
      
      const { data, error } = await supabase
        .from('work_orders')
        .update(workOrder)
        .eq('id', workOrder.id)
        .select()
        .single();
      
      if (error) throw error;
      
      toast({
        title: 'Work Order updated',
        description: 'Work Order has been updated successfully.',
        variant: 'success',
      });
      
      if (selectedWorkOrder?.id === workOrder.id) {
        await fetchWorkOrderById(workOrder.id);
      }
      
      await fetchWorkOrders();
      return data;
    } catch (error: any) {
      toast({
        title: 'Error updating work order',
        description: error.message,
        variant: 'destructive',
      });
      return null;
    } finally {
      setLoading(false);
    }
  }, [fetchWorkOrders, fetchWorkOrderById, setLoading, toast]);

  /**
   * Delete a Work Order
   */
  const deleteWorkOrder = useCallback(async (id: UUID) => {
    try {
      setLoading(true);
      
      // Check if the work order is in a deletable state (Draft or Cancelled)
      const { data: workOrder, error: fetchError } = await supabase
        .from('work_orders')
        .select('status')
        .eq('id', id)
        .single();
      
      if (fetchError) throw fetchError;
      
      if (!workOrder) {
        throw new Error('Work order not found');
      }
      
      if (!['Draft', 'Cancelled'].includes(workOrder.status)) {
        throw new Error('Only work orders in Draft or Cancelled status can be deleted');
      }
      
      // Perform the deletion
      const { error } = await supabase
        .from('work_orders')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
      
      toast({
        title: 'Work Order deleted',
        description: 'Work Order has been deleted successfully.',
        variant: 'success',
      });
      
      // Refresh the work orders list
      await fetchWorkOrders();
      
      return true;
    } catch (error: any) {
      toast({
        title: 'Error deleting work order',
        description: error.message,
        variant: 'destructive',
      });
      return false;
    } finally {
      setLoading(false);
    }
  }, [fetchWorkOrders, setLoading, toast]);

  return {
    loading,
    workOrders,
    selectedWorkOrder,
    fetchWorkOrders,
    fetchWorkOrderById,
    createWorkOrder,
    updateWorkOrder,
    deleteWorkOrder
  };
};
